import { ComponentType } from 'react';
import { setAlignment, setDirection, setIndentation } from 'roosterjs-editor-api';
import { Alignment, Direction, Indentation } from 'roosterjs-editor-types';
import { BackgroundColorAction } from './background-color';
import { FontColorAction } from './font-color';
import { FontFamilyAction } from './font-family';
import { FontSizeAction } from './font-size';
import { HeadingAction } from './heading';
import { InsertHyperlinkAction } from './insert-hyperlink';
import { InsertImageAction } from './insert-image';
import { InsertTableAction } from './insert-table';
import { createSimpleAction } from './simple-action-builder';
import { IEditorToolbarActionBaseProps } from './types';

export * from './types';

export const Actions: ComponentType<IEditorToolbarActionBaseProps>[] = [
    createSimpleAction({
        action: 'toggleBold',
        activeStatePropName: 'isBold',
        iconName: 'ap-bold.rte',
        iconSize: '14',
        tooltip: 'Bold'
    }),
    createSimpleAction({
        action: 'toggleItalic',
        activeStatePropName: 'isItalic',
        iconName: 'ap-italic.rte',
        iconSize: '14',
        tooltip: 'Italic'
    }),
    createSimpleAction({
        action: 'toggleUnderline',
        activeStatePropName: 'isUnderline',
        iconName: 'ap-underline.rte',
        iconSize: '14',
        tooltip: 'Underline'
    }),
    FontFamilyAction,
    FontSizeAction,
    FontColorAction,
    BackgroundColorAction,
    createSimpleAction({
        action: 'toggleBullet',
        activeStatePropName: 'isBullet',
        iconName: 'ap-bullet.rte',
        tooltip: 'Bullet'
    }),
    createSimpleAction({
        action: 'toggleNumbering',
        activeStatePropName: 'isNumbering',
        iconName: 'ap-numbering.rte',
        tooltip: 'Numbering'
    }),
    createSimpleAction({
        iconName: 'ap-decrease-indent.rte',
        tooltip: 'Decrease Indent',
        onTriggerAction: (editor) => setIndentation(editor, Indentation.Decrease)
    }),
    createSimpleAction({
        iconName: 'ap-increase-indent.rte',
        tooltip: 'Increase Indentation',
        onTriggerAction: (editor) => setIndentation(editor, Indentation.Increase)
    }),
    createSimpleAction({
        action: 'toggleBlockQuote',
        activeStatePropName: 'isBlockQuote',
        iconName: 'ap-quotes.rte',
        tooltip: 'Quote'
    }),
    createSimpleAction({
        iconName: 'ap-align-left.rte',
        tooltip: 'Left Align',
        onTriggerAction: (editor) => setAlignment(editor, Alignment.Left)
    }),
    createSimpleAction({
        iconName: 'ap-align-center.rte',
        tooltip: 'Center Align',
        onTriggerAction: (editor) => setAlignment(editor, Alignment.Center)
    }),
    createSimpleAction({
        iconName: 'ap-align-right.rte',
        tooltip: 'Right Align',
        onTriggerAction: (editor) => setAlignment(editor, Alignment.Right)
    }),
    InsertHyperlinkAction,
    createSimpleAction({
        action: 'removeLink',
        iconName: 'ap-remove-link.rte',
        tooltip: 'Remove Link',
        isHidden: ({ canUnlink }) => !canUnlink
    }),
    InsertImageAction,
    InsertTableAction,
    createSimpleAction({
        action: 'toggleSuperscript',
        activeStatePropName: 'isSuperscript',
        iconName: 'ap-superscript.rte',
        tooltip: 'Superscript'
    }),
    createSimpleAction({
        action: 'toggleSubscript',
        activeStatePropName: 'isSubscript',
        iconName: 'ap-subscript.rte',
        tooltip: 'Subscript'
    }),
    createSimpleAction({
        action: 'toggleStrikethrough',
        activeStatePropName: 'isStrikeThrough',
        iconName: 'ap-strikethrough.rte',
        tooltip: 'Strike'
    }),
    HeadingAction,
    createSimpleAction({
        action: 'toggleCodeBlock',
        iconName: 'ap-code-snippet.rte',
        tooltip: 'Code Block'
    }),
    createSimpleAction({
        iconName: 'ap-left-to-right.rte',
        tooltip: 'Left to Right',
        onTriggerAction: (editor) => setDirection(editor, Direction.LeftToRight)
    }),
    createSimpleAction({
        iconName: 'ap-right-to-left.rte',
        tooltip: 'Right to Left',
        onTriggerAction: (editor) => setDirection(editor, Direction.RightToLeft)
    }),
    createSimpleAction({
        iconName: 'ap-undo.rte',
        tooltip: 'Undo',
        isDisabled: ({ canUndo }) => !canUndo,
        onTriggerAction: (editor) => editor.undo()
    }),
    createSimpleAction({
        iconName: 'ap-redo.rte',
        tooltip: 'Redo',
        isDisabled: ({ canRedo }) => !canRedo,
        onTriggerAction: (editor) => editor.redo()
    }),
    createSimpleAction({
        action: 'clearFormat',
        iconName: 'ap-remove-formatting.rte',
        tooltip: 'Clear Format'
    }),
    createSimpleAction({
        iconName: 'ap-export.rte',
        tooltip: 'Export',
        onTriggerAction: (editor) => {
            const win = window.open('about:blank', '_blank');
            if (!win) {
                return;
            }
            win.document.body.innerHTML = editor.getContent();
        }
    }),
    createSimpleAction({
        iconName: 'ap-clear.rte',
        tooltip: 'Clear',
        onTriggerAction: (editor) => editor.setContent('', true)
    })
];
